import React, { useMemo, useState } from "react";
import {
  Box,
  EntitySelectorSectionLabel,
  Icon,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Popover,
  PopoverPopup,
  PopoverTrigger,
  QuietRow,
  ROW_RUNGS,
  ScrollArea,
  Text,
} from "@salestrak/ui";

import {
  AccessLevelMenu,
  EditAllMenu,
} from "#app/components/sharing/access-level-menu";
import {
  ACCESS_ROW_HEIGHT,
  AccessRowFrame,
} from "#app/components/sharing/access-row";
import { ListRowsSkeleton } from "#app/components/loading/list-rows-skeleton";
import { useMemberAccess } from "#app/lib/hooks/use-member-access";
import type { ObjectAccessLevel } from "#app/lib/data";
import type { EntityData } from "#app/lib/entity-visuals";
import type { Omittable } from "@salestrak/ui";

// Per-user access panel — the popover behind the settings members table's
// Access cell. Data-inverse of the share dialogs: instead of "who can see
// this object", it lists the workflows or items THIS user has explicit
// access to, in the share dialog's row grammar (entity, name, one level
// menu per row). The cell's two count buttons each open their own panel:
// the button already names the noun, so the panel carries no tabs.
// Workflows additionally list the rest of the workspace's workflows as
// click-to-add rows; items only list what is granted (that universe is
// unbounded).

export type MemberAccessKind = "workflows" | "items";

// Granted rows are the access list's rung; candidate rows are picker rows.
const CANDIDATE_ROW_HEIGHT = ROW_RUNGS.control;
// The level menu's column, fixed so the menus line up whatever the tier.
const ACCESS_SLOT_WIDTH = 116;
// A search well only earns its rung once the list is long enough to lose
// a row in.
const SEARCH_MIN_ROWS = 7;
// Ten granted rows before the list scrolls.
const LIST_MAX_HEIGHT = ACCESS_ROW_HEIGHT * 10;

// Count button rendered in the members table's Access cell — each one
// anchors its own surface and opens the panel for its noun. On a phone the
// popover is a sheet captioned "Access".
export function MemberAccessCountButton({
  userId,
  kind,
  count,
  label,
}: {
  userId: string;
  kind: MemberAccessKind;
  count: number;
  label: string;
}) {
  // The count reads on the key ink and the noun on the muted rung: the
  // number is the value, the noun only names what was counted. A zero
  // count reads muted throughout, so the non-zero button is the one that
  // draws the eye. 11px on a 20px face with 5px insets — one rung under
  // the 12px cell text, so two of these ride a table cell without
  // competing with it.
  const trigger = (
    <Box
      as="button"
      type="button"
      direction="row"
      align="center"
      height={20}
      rounded="sm"
      hoverBackground
      cursor="pointer"
      className="px-[5px]"
    >
      <Text
        size={null}
        variant={count === 0 ? "muted" : undefined}
        className="pr-[5px] text-[11px]"
      >
        {count}
      </Text>
      <Text size={null} variant="muted" className="text-[11px]">
        {label}
      </Text>
    </Box>
  );

  return (
    // `responsive`: a bottom sheet on a phone, captioned "Access".
    <Popover responsive>
      <PopoverTrigger render={trigger} />
      <PopoverPopup align="start" padding="sm" sheetTitle="Access" width="builder">
        <MemberAccessPanel userId={userId} kind={kind} />
      </PopoverPopup>
    </Popover>
  );
}

// One row of either list, whatever the noun: the entity it renders as,
// its tier, and how the access arrived.
type AccessRow = Omittable<{
  id: string;
  name: string;
  entity: EntityData;
  accessLevel: ObjectAccessLevel;
  viaTeam: boolean;
  meta?: string;
}>;

export function MemberAccessPanel({
  userId,
  kind,
}: Omittable<{
  userId: string;
  kind: MemberAccessKind;
}>) {
  const {
    loading,
    accessList,
    setWorkflowAccess,
    setItemAccess,
    setAllWorkflows,
    setAllItems,
    removeAllWorkflows,
    removeAllItems,
  } = useMemberAccess(userId);

  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const matches = (row: AccessRow) => !q || row.name.toLowerCase().includes(q);

  const granted = useMemo<AccessRow[]>(() => {
    if (kind === "workflows") {
      return (accessList?.workflows ?? []).map((w): AccessRow => ({
        id: w.workflowId,
        name: w.name,
        entity: {
          kind: "workflow",
          id: w.workflowId,
          workflow: { name: w.name, icon: { icon: w.icon, color: w.iconColor } },
        },
        accessLevel: w.accessLevel,
        viaTeam: w.viaTeam,
        meta: w.viaTeam ? "Via team" : undefined,
      }));
    }
    return (accessList?.items ?? []).map((i): AccessRow => ({
      id: i.issueId,
      name: i.name,
      entity: { kind: "issue", id: i.issueId, issue: { name: i.name } },
      accessLevel: i.accessLevel,
      viaTeam: i.viaTeam,
      // Where the item lives; a team grant says how instead.
      meta: i.viaTeam ? "Via team" : (i.workflowName ?? undefined),
    }));
  }, [accessList, kind]);

  const candidates = useMemo<AccessRow[]>(() => {
    if (kind !== "workflows") return [];
    return (accessList?.availableWorkflows ?? []).map((w): AccessRow => ({
      id: w.workflowId,
      name: w.name,
      entity: {
        kind: "workflow",
        id: w.workflowId,
        workflow: { name: w.name, icon: { icon: w.icon, color: w.iconColor } },
      },
      // The tier an add grants: the workflow's default, or the tier a
      // removed row remembers.
      accessLevel: w.accessLevel,
      viaTeam: false,
    }));
  }, [accessList, kind]);

  const setAccess = kind === "workflows" ? setWorkflowAccess : setItemAccess;
  const setAll = kind === "workflows" ? setAllWorkflows : setAllItems;
  const removeAll = kind === "workflows" ? removeAllWorkflows : removeAllItems;

  if (loading && !accessList) {
    // The panel's own shape — a short list with a trailing control — so the
    // rows land on the bars. Immediate: the popover is already open.
    return <ListRowsSkeleton rows={3} rowHeight={ACCESS_ROW_HEIGHT} trailing />;
  }

  const total = granted.length + candidates.length;
  const visibleGranted = granted.filter(matches);
  const visibleCandidates = candidates.filter(matches);

  return (
    <Box>
      {total >= SEARCH_MIN_ROWS ? (
        <Box paddingBottom="xs">
          <InputGroup variant="outline">
            <InputGroupAddon>
              <Icon name="search" />
            </InputGroupAddon>
            <InputGroupInput
              type="search"
              placeholder="Search..."
              value={query}
              onValueChange={setQuery}
            />
          </InputGroup>
        </Box>
      ) : null}

      <Box direction="column" maxHeight={LIST_MAX_HEIGHT}>
        <ScrollArea>
          {total === 0 ? (
            <Box paddingX="sm"><QuietRow height={ACCESS_ROW_HEIGHT}>Nothing shared directly.</QuietRow></Box>
          ) : visibleGranted.length === 0 && visibleCandidates.length === 0 ? (
            <Box paddingX="sm"><QuietRow height={ACCESS_ROW_HEIGHT}>No matches.</QuietRow></Box>
          ) : null}

          {visibleGranted.length > 0 ? (
            <>
              <EntitySelectorSectionLabel
                label="Has access"
                count={visibleGranted.length}
                trailing={
                  <EditAllMenu
                    setAllLabel="Set all to"
                    onSetAll={setAll}
                    onRemoveAll={removeAll}
                  />
                }
              />
              {visibleGranted.map((row) => (
                <GrantedRow
                  key={row.id}
                  row={row}
                  onChange={(level) => setAccess(row.id, level, true)}
                  // A team-derived grant comes off on the team, not here.
                  onRemove={
                    row.viaTeam
                      ? undefined
                      : () => setAccess(row.id, row.accessLevel, false)
                  }
                />
              ))}
            </>
          ) : null}

          {visibleCandidates.length > 0 ? (
            <>
              <EntitySelectorSectionLabel label="Add access" />
              {visibleCandidates.map((row) => (
                <CandidateRow
                  key={row.id}
                  row={row}
                  onAdd={() => setAccess(row.id, row.accessLevel, true)}
                />
              ))}
            </>
          ) : null}
        </ScrollArea>
      </Box>
    </Box>
  );
}

// A granted row: the shared frame with the one control — the level menu,
// which also carries Remove. A team-derived tier reads muted; picking a
// tier there writes the user's own share, which wins over the team's.
function GrantedRow({
  row,
  onChange,
  onRemove,
}: Omittable<{
  row: AccessRow;
  onChange: (level: ObjectAccessLevel) => void;
  onRemove?: () => void;
}>) {
  return (
    <Box paddingX="sm">
      <AccessRowFrame entity={row.entity} label={row.name} meta={row.meta}>
        <Box direction="row" justify="end" width={ACCESS_SLOT_WIDTH}>
          <AccessLevelMenu
            value={row.accessLevel}
            inherited={row.viaTeam}
            onChange={onChange}
            onRemove={onRemove}
          />
        </Box>
      </AccessRowFrame>
    </Box>
  );
}

// A candidate row is the action: pressing it grants the default tier. The
// faint plus on the trailing edge says so; the row's wash answers the press.
function CandidateRow({ row, onAdd }: { row: AccessRow; onAdd: () => void }) {
  return (
    <Box
      as="button"
      width="100%"
      paddingX="sm"
      rounded="sm"
      hoverBackground
      cursor="pointer"
      aria-label={`Add access to ${row.name}`}
      onClick={onAdd}
    >
      <AccessRowFrame
        entity={row.entity}
        label={row.name}
        height={CANDIDATE_ROW_HEIGHT}
      >
        <Icon name="plus" size="sm" color="faint" />
      </AccessRowFrame>
    </Box>
  );
}

